/*
 *     Copyright (c) 2020 Bcame S.r.l.. All rights reserved.
 *     Bcame S.r.l. Technology Business Unit
 *
 *     Company confidential.
 *
 *     This software is proprietary  to and embodies the confidential
 *     technology of Bcame S.r.l..  Possession, use, or copying of this
 *     software and media is authorized only pursuant to a valid written
 *     license from Bcame S.r.l.  or an authorized sublicensor.
 *
 *     Bcame S.r.l.
 */

import { DefaultShipping, DefaultBilling } from '@/store/modules/checkout/defaultValues'
import { EventBus, graphqlManageError } from '@/helpers'
import { getCookie, writeOperationLog } from 'theme/helpers'
import { clearAllCache } from '@/store/lib/search'
import Logger from '@/services/Logger'
import Config from 'config'
import Graphql from '@/services/Graphql'
import HttpProvider from '@/services/HttpProvider'

export default {
  /**
   * Login user and return user profile and current token
   */
  afterLogin (ctx, token) {
    if (token.length) {
      let callResponse = token[0]
      ctx.commit('setAdminRole', callResponse)
      ctx.commit('setAdminToken', callResponse.token)
      ctx.commit('setStoreCode', callResponse.userStore)
      ctx.commit('setUserType', callResponse.userType)
      ctx.commit('setUserStoreId', callResponse.userStoreId)
      ctx.commit('setStoreId', callResponse.storeId)
      ctx.commit('setCoupons', callResponse.coupons)
      let onlineStore = callResponse.userOnlineStore || Config.Theme.onlineStore.code || 'os_en'
      ctx.commit('setOnlineStoreCode', onlineStore)
      ctx.dispatch('emptyCheckout')
      clearAllCache()
      return ctx.dispatch('me', { token, invalid: true, fromLogin: true, boutiqueCode: callResponse.userStore })
        .then(() => {
          EventBus.$emit('userAdmin:isLoggedIn')
          return ctx.state.current
        })
    }
  },
  login (ctx, { username, password }) {
    return HttpProvider.post(
      Config.API.users.login.replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode),
      { username, password },
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then((resp) => {
        if (resp.status === 200) {
          return ctx.dispatch('afterLogin', resp.data)
        }
        return resp.data
      })
  },
  me (context, { token, invalid = false, fromLogin, boutiqueCode }) {
    if (context.state.current && !invalid) {
      return Promise.resolve(context.state.current)
    } else {
      let url = Config.API.storeLocator.singleBoutique.replace('{{code}}', boutiqueCode)
      return HttpProvider.get(url)
        .then(resp => {
          if (resp && resp.data && resp.data.length) {
            return resp.data[0]
          } else {
            return []
          }
        })
        .then((resp) => {
          // if has default shipping use it
          let defaultShipping = { ...DefaultShipping, ...resp }
          let defaultBilling = { ...DefaultBilling, ...resp }
          context.dispatch('presetAddressCheckout', { mode: 'shipping', mutationName: 'setShippingAddress', defaultAddress: defaultShipping })
          context.dispatch('presetAddressCheckout', { mode: 'billing', mutationName: 'setBillingAddress', defaultAddress: defaultBilling })
          context.commit('setAdmin', resp || null)
        })
    }
  },
  /**
   * Register user and return user profile and current token
   */
  create (context, data) {
    var params = {
      password: data.password,
      email: data.email,
      firstname: data.firstname,
      lastname: data.lastname,
      dob: data.dob,
      profiling: data.privacy.terms2,
      marketing: data.privacy.terms3,
      coin_card: data.coinCard
    }
    return HttpProvider.post(
      Config.API.users.create.replace('{{storeViewCode}}', context.rootState.ui.storeViewCode),
      params,
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then((resp) => {
        writeOperationLog('user-creation', context.rootGetters['userAdmin/getCurrentAgentID'])
        if (resp.status === 200) {
          // user created
          return true
        }
        throw new Error('user not found')
      })
      .catch(err => {
        throw err
      })
  },
  /**
   * Logout user
   */
  logout (context) {
    context.commit('setAdmin', null)
    context.commit('setAdminToken', null)
    context.commit('setAdminRole', null)
    context.dispatch('cart/empty', null, { root: true })
    context.commit('ui/hideLoader', true, { root: true })
    context.dispatch('emptyCheckout')
    EventBus.$emit('userAdmin:loggedOut', true)
    // clean cached call
    clearAllCache()
    let url = Config.API.users.logout
      .replace('{{storeViewCode}}', context.rootState.ui.storeViewCode)
    return HttpProvider.post(url)
      .finally(() => {
        global.$router.push({ name: 'login' })
      })
  },
  /**
   * Update user data
   */
  update (context, data) {
    // clean for update
    delete data.default_shipping
    delete data.default_billing
    delete data.addresses
    delete data.wishlist
    delete data.id
    let token = context.state.token
    return Graphql.genericCall(
      {},
      token,
      context.rootState.ui.storeViewCode,
      'updateCustomer',
      {
        customerInput: data
      },
      false)
      .then(resp => {
        if (resp.data && resp.data.updateCustomer && resp.data.updateCustomer) {
          let customer = resp.data.updateCustomer.customer
          context.commit('setAdmin', customer)
          return resp.data.updateCustomer.customer
        } else {
          throw new Error(resp)
        }
      })
      .catch(err => {
        Logger.error(err)
        throw new Error('GenericError')
      })
  },
  /**
   * Check if user email exist
   */
  isEmailAvailable (ctx, { email }) {
    let query = Config.API.users.emailAvailable.replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode)
    return HttpProvider.post(
      query,
      { customerEmail: email },
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then(res => {
        return res.data
      })
  },
  /**
   * change password
   */
  changePass (ctx, data) {
    return Graphql.genericCall(
      {},
      ctx.state.token,
      ctx.rootState.ui.storeViewCode,
      'customerChangePassword',
      data,
      false)
      .then(res => {
        if (graphqlManageError(res)) {
          // eslint-disable-next-line
          throw { message: graphqlManageError(res) }
        }
      })
  },
  updateAddressList (ctx, { address, index }) {
    let params = { ...ctx.state.current, addresses: [ ...ctx.state.current.addresses ] }
    if (index !== null) {
      params.addresses[index] = address
    } else {
      params.addresses.push(address)
    }
    // to do remove after change backend -- fix region
    if (address.region && address.region.region_code) {
      const region = ctx.rootGetters['checkout/getRegionByCode']({ regionCode: address.region.region_code, countryID: address.country_id })
      address.region = {
        region: region.name,
        region_id: Number(region.id),
        region_code: region.code
      }
      delete address.region_id
    }
    let id = address.id
    delete address.id

    let token = ctx.state.token
    return Graphql.genericCall(
      {},
      token,
      ctx.rootState.ui.storeViewCode,
      id ? 'updateAddress' : 'createAddress',
      {
        id: id,
        AddressInput: address
      },
      false)
      .then(resp => {
        let customerAddress = resp && resp.data ? resp.data.updateCustomerAddress || resp.data.createCustomerAddress : null
        if (customerAddress) {
          ctx.commit('setAddress', customerAddress)
          return ctx.state.current
        } else {
          throw new Error(resp)
        }
      })
      .catch(err => {
        Logger.error(err)
        throw new Error('GenericError')
      })
    // return ctx.dispatch('update', params)
  },
  deleteAddress (ctx, id) {
    // var params = {}
    /* assign(params, ctx.state.current)
    if (index !== null) {
      params.addresses.splice(index, 1)
    } */
    let token = ctx.state.token
    return Graphql.genericCall(
      {},
      token,
      ctx.rootState.ui.storeViewCode,
      'deleteAddress',
      {
        id
      },
      false)
      .then(resp => {
        if (graphqlManageError(resp)) {
          // eslint-disable-next-line
          throw { body: { result: graphqlManageError(resp) } }
        }
        if (resp && resp.data && resp.data.deleteCustomerAddress) {
          ctx.commit('deleteAddress', id)
        }
      })
  },
  // remove checkout last information from cache
  emptyCheckout (ctx) {
    ctx.dispatch('checkout/empty', null, { root: true })
  },
  presetAddressCheckout (ctx, { mode, mutationName, addresses }) {
    // preset address checkout if not setted
    if (ctx.rootState.checkout[`${mode}Address`].firstname === '') {
      ctx.commit(`checkout/${mutationName}`, addresses, { root: true })
    }
  },
  moveCartFromGuest (ctx) {
    if (ctx.rootGetters['cart/isEmptyCart']) {
      return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
    }
    // get guest cartid
    return ctx.dispatch('cart/mergeCart', { guestCartID: ctx.rootState.cart.cartId }, { root: true })
      .then(() => {
        return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
      })
      .catch(() => {
        return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
      })
  },
  forgotpassword (ctx, { email }) {
    return HttpProvider.put(
      Config.API.users.resetPassword
        .replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode),
      {
        email,
        template: 'email_reset',
        websiteId: ctx.rootState.storeConfig.config.website_id
      }
    )
      .then(res => res.data)
  },
  resetPassword (ctx, { email, resetToken, newPassword }) {
    return HttpProvider.post(
      Config.API.users.resetPasswordFinalStep
        .replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode),
      {
        email,
        resetToken,
        newPassword
      }
    )
      .then(res => res.data)
  },
  /**
   * reorder:  populate basket with selected order
   */
  reorder (ctx, { id }) {
    return HttpProvider.get(
      Config.API.order.reorder
        .replace('{{storeViewCode}}', ctx.rootState.ui.storeViewCode)
        .replace('{{orderId}}', id))
      .then(resp => {
        if (resp.status === 200 && resp.data === 'ok') {
          return ctx.dispatch('cart/syncServerCart', { invalid: true }, { root: true })
        }
        throw new Error(resp)
      })
  },
  unsubscribe (ctx, { email }) {
    return Graphql.genericCall(
      {},
      process.env.VUE_APP_MAGENTO_TOKEN,
      ctx.rootState.ui.storeViewCode,
      'unsubscribeEmail',
      {
        customerInput: {
          email,
          profiling: false
        }
      },
      false)
      .then(resp => {
        if (graphqlManageError(resp)) {
          // eslint-disable-next-line
          throw { body: { result: graphqlManageError(resp) } }
        }
        if (resp.data) {
          let updateCustomer = { ...ctx.state.current, profiling: resp.data.Bcame_set_profiling }
          ctx.commit('setAdmin', updateCustomer)
          return true
        } else {
          throw new Error(resp)
        }
      })
      .catch(err => {
        Logger.error(err)
        throw new Error('GenericError')
      })
  },
  emailAvailable (ctx, email) {
    return HttpProvider.post(
      Config.API.users.emailAvailable.replace(
        '{{storeViewCode}}', ctx.rootState.ui.storeViewCode
      ),
      { customerEmail: email },
      {
        headers: {
          'Authorization': `Bearer ${process.env.VUE_APP_MAGENTO_TOKEN}`
        }
      })
      .then(res => {
        console.log('res', res)
      })
  },
  init (ctx) {
    const promiseArr = []
    if (ctx.state.token && ctx.state.storeCode) {
      if (process.client) {
        const adminStore = require('theme/store/lib/storage').userAdminStore
        adminStore.getItem('coupons').then((coupons) => {
          ctx.commit('setCoupons', coupons)
        })
        return adminStore.getItem('currentAdmin').then((admin) => {
          ctx.commit('setAdmin', admin)
          return ctx.dispatch('me', { token: ctx.state.token, invalid: false, boutiqueCode: ctx.state.storeCode })
        })
      }
      promiseArr.push(ctx.dispatch('me', { token: ctx.state.token, invalid: true, boutiqueCode: ctx.state.storeCode }))
    } else {
      const token = getCookie('admin-token')
      const storeCode = getCookie('store-code')
      const userRole = getCookie('admin-role')
      ctx.commit('setAdminToken', token)
      ctx.commit('setAdminRole', userRole)
      if (token && storeCode) {
        ctx.commit('setStoreCode', storeCode)
        promiseArr.push(ctx.dispatch('me', { token, invalid: true, boutiqueCode: storeCode }))
      } else {
        promiseArr.push(ctx.dispatch('logout'))
      }
    }
    return Promise.all(promiseArr)
  }
}
